import { AddCircleOutlined, DeleteOutlined, EditOutlined, RedeemOutlined } from "@mui/icons-material";
import { Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import {
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_BODY_ROW_SX,
	CONFIG_TABLE_CONTAINER_SX,
	CONFIG_TABLE_HEADER_CELL_SX,
} from "../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useDeleteIncentiveConfiguration, useIncentiveConfigurations } from "../../../../hooks/useIncentiveConfigurations";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import type { IncentiveConfiguration, IncentivePlan } from "../../../../utils/models";
import BonusPlanDialog from "./BonusPlanDialog";

const windowChipLabel = (plan: IncentivePlan): string => {
	const start = plan.startHour;
	const end = plan.endHour;
	if (end == null) return `${start}h+: $${plan.value}`;
	return `${start}–${end}h: $${plan.value}`;
};

type Props = { departmentId: string };

const BonusPlanSection: FC<Props> = ({ departmentId }) => {
	const { t } = useTranslation();
	// Same gate as the scheduler's "Incentive Pay" block in CreateShiftContent — creating plans on
	// a dept where shift bonuses are off would just orphan the config (no shift could ever pick it).
	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);
	const { isAnyDemo } = useDepartmentFlags();
	const isVisible = isShiftBonusesEnabled || isAnyDemo;

	const { data: configurations = [], isLoading, isError } = useIncentiveConfigurations(isVisible ? departmentId : undefined);
	const deleteMutation = useDeleteIncentiveConfiguration(departmentId);

	const [editing, setEditing] = useState<IncentiveConfiguration | undefined>(undefined);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState<IncentiveConfiguration | null>(null);

	const openCreate = () => {
		setEditing(undefined);
		setDialogOpen(true);
	};

	const openEdit = (config: IncentiveConfiguration) => {
		setEditing(config);
		setDialogOpen(true);
	};

	const handleDelete = async () => {
		if (!confirmDelete) return;
		try {
			await deleteMutation.mutateAsync(confirmDelete.id);
			setConfirmDelete(null);
		} catch {
			// onError in the hook surfaces the failure via the error snackbar; keep the confirm dialog open for retry.
		}
	};

	if (!isVisible) return null;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
			<SettingsSection
				headerOnly
				title={t("incentive.section.title")}
				dataTestId="dept-config-rates-bonus-plan-header"
				action={
					<SystemButton onClick={openCreate} buttonVariant="primary" size="small" leadingIcon={<AddCircleOutlined />} data-testid="bonus-plan-add-button">
						{t("incentive.add.plan")}
					</SystemButton>
				}
			/>

			<TableContainer component={Paper} sx={CONFIG_TABLE_CONTAINER_SX}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: 200 }]}>{t("name")}</TableCell>
							<TableCell sx={CONFIG_TABLE_HEADER_CELL_SX}>{t("incentive.windows")}</TableCell>
							<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: 120 }]}>{t("actions")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isError && (
							<TableRow>
								<TableCell colSpan={3} align="center" sx={{ padding: "2rem" }}>
									<Typography variant="body2" color="error">
										{t("incentive.section.load.failed")}
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{!isError && !isLoading && configurations.length === 0 && (
							<TableRow>
								<TableCell colSpan={3} sx={{ borderBottom: "none", p: 0 }}>
									<EmptyState
										icon={<RedeemOutlined />}
										title={t("incentive.section.empty.title")}
										hint={t("incentive.section.empty.hint")}
										action={
											<SystemButton
												buttonVariant="secondary"
												size="small"
												leadingIcon={<AddCircleOutlined fontSize="small" />}
												onClick={openCreate}
												data-testid="bonus-plan-empty-add"
											>
												{t("incentive.add.plan")}
											</SystemButton>
										}
										dataTestId="bonus-plan-empty"
									/>
								</TableCell>
							</TableRow>
						)}
						{!isError &&
							configurations.map((config) => (
								<TableRow key={config.id} data-testid={`bonus-plan-row-${config.id}`} sx={CONFIG_TABLE_BODY_ROW_SX}>
									<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { padding: "8px 12px", verticalAlign: "middle" }]}>{config.name}</TableCell>
									<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { padding: "8px 12px", verticalAlign: "middle" }]}>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{[...config.plans]
												.sort((a, b) => a.startHour - b.startHour)
												.map((plan) => (
													<Chip key={plan.id ?? `${plan.startHour}-${plan.endHour}`} label={windowChipLabel(plan)} size="small" variant="outlined" />
												))}
										</Box>
									</TableCell>
									<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { padding: "8px 12px", verticalAlign: "middle" }]}>
										<Box sx={{ display: "flex", gap: 0.5 }}>
											<SystemButton
												buttonVariant="borderless"
												size="small"
												leadingIcon={<EditOutlined fontSize="small" />}
												onClick={() => openEdit(config)}
												tooltip={t("edit")}
												aria-label={t("edit")}
												data-testid={`bonus-plan-edit-${config.id}`}
											/>
											<SystemButton
												buttonVariant="borderless"
												size="small"
												leadingIcon={<DeleteOutlined fontSize="small" />}
												onClick={() => setConfirmDelete(config)}
												tooltip={t("delete")}
												aria-label={t("delete")}
												data-testid={`bonus-plan-delete-${config.id}`}
											/>
										</Box>
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>

			<BonusPlanDialog open={dialogOpen} onClose={() => setDialogOpen(false)} departmentId={departmentId} configuration={editing} />

			<SystemDialog
				open={!!confirmDelete}
				onClose={() => setConfirmDelete(null)}
				title={t("incentive.delete.plan.confirm.title")}
				body={t("incentive.delete.plan.confirm.body", { name: confirmDelete?.name ?? "" })}
				confirm={t("delete")}
				onConfirm={handleDelete}
				confirmLoading={deleteMutation.isPending}
				dataTestId="bonus-plan-delete-dialog"
			/>
		</Box>
	);
};

export default BonusPlanSection;
